import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import { selectors, updateBlueprintDates } from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import Field from '../../SessionSettingsShared/components/Field';

// Backend expects ISO strings it can parse with datetime.fromisoformat(...rstrip('Z')).
const DATE_FMT = 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]';

/**
 * Blueprint-authoring only: edit the template's absolute historical window after
 * create. Rendered only in blueprint mode (GeneralViewV2 gates it) so trainer
 * settings are unchanged. Editing is blocked once the version is locked — the
 * authoring view wraps the whole settings pane in a read-only overlay when
 * locked, and the backend rejects the PATCH (partial_update is a locked write).
 */
const BlueprintDatesCard: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const waiting = useSelector(selectors.blueprintsWaiting);

  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Seed / re-sync from the saved session (incl. after a successful save).
  useEffect(() => {
    if (session?.start_date && session?.end_date) {
      setRange([dayjs(session.start_date), dayjs(session.end_date)]);
    }
  }, [session?.start_date, session?.end_date]);

  const [start, end] = range || [];
  const dirty =
    !!start &&
    !!end &&
    (start.format(DATE_FMT) !== dayjs(session?.start_date).format(DATE_FMT) ||
      end.format(DATE_FMT) !== dayjs(session?.end_date).format(DATE_FMT));

  return (
    <SettingsCard
      title="Historical period"
      className="set3b-blueprint-dates-card"
    >
      <Field
        label="Trading window"
        hint="The absolute historical dates this scenario replays."
      >
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          value={range ?? undefined}
          allowClear={false}
          // Weekends are rejected by the backend (and would break historical resume).
          disabledDate={(d: Dayjs) => d.day() === 0 || d.day() === 6}
          onChange={vals => setRange((vals as [Dayjs, Dayjs] | null) ?? null)}
        />
      </Field>
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <Button
          type="primary"
          size="small"
          loading={waiting}
          disabled={!dirty}
          onClick={() => {
            if (!start || !end) return;
            dispatch(
              updateBlueprintDates({
                id: session.id,
                start_date: start.format(DATE_FMT),
                end_date: end.format(DATE_FMT),
              }),
            );
          }}
        >
          Save period
        </Button>
      </div>
    </SettingsCard>
  );
};

export default BlueprintDatesCard;
